(() => {
  'use strict';
  if (!window.customElements || customElements.get('aroha-labour-weekend')) return;
  class ArohaLabourWeekend extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.abort = new AbortController();
      this.initialize();
    }
    disconnectedCallback() {
      clearInterval(this.rotationTimer);
      clearInterval(this.windowTimer);
      this.abort?.abort();
      this.initialized = false;
    }
    initialize() {
      this.slides = [...this.querySelectorAll('.aa-lw-slide')];
      this.dots = [...this.querySelectorAll('[data-lw-slide]')];
      this.viewport = this.querySelector('.aa-lw-slides');
      this.controls = this.querySelector('.aa-lw-controls');
      this.toggle = this.querySelector('[data-lw-toggle]');
      this.phase = this.querySelector('[data-lw-phase]');
      if (!this.slides.length || !this.viewport || !this.controls || !this.toggle || !this.phase) return;
      this.initialized = true;
      this.index = 0;
      this.userPaused = false;
      this.hovering = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const options = { signal: this.abort.signal };
      const on = (target, event, listener) => target.addEventListener(event, listener, options);
      on(this.querySelector('[data-lw-previous]'), 'click', () => this.go(this.index - 1, true));
      on(this.querySelector('[data-lw-next]'), 'click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.go(index, true)));
      on(this.toggle, 'pointerdown', () => { this.pointerToggleState = !this.userPaused; });
      on(this.toggle, 'click', () => {
        this.userPaused = this.pointerToggleState ?? !this.userPaused;
        this.pointerToggleState = undefined;
        this.refreshRotation();
      });
      on(this, 'mouseenter', () => { this.hovering = true; this.refreshRotation(); });
      on(this, 'mouseleave', () => { this.hovering = false; this.refreshRotation(); });
      // Once keyboard focus enters, do not resume without an explicit Play action.
      on(this, 'focusin', () => { this.userPaused = true; this.refreshRotation(); });
      on(this, 'keydown', event => {
        if (!this.controls.contains(event.target) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const actions = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in actions)) return;
        event.preventDefault();
        this.go(actions[event.key], true);
      });
      on(document, 'visibilitychange', () => { this.updateWindow(); this.refreshRotation(); });
      on(window, 'pageshow', () => { this.updateWindow(); this.refreshRotation(); });
      on(window, 'resize', () => this.equalizeSlideHeight());
      on(this.motion, 'change', () => this.refreshRotation());
      this.setAttribute('aria-roledescription', 'carousel');
      this.go(0, false);
      this.equalizeSlideHeight();
      this.controls.hidden = this.slides.length < 2;
      this.updateWindow();
      // Covers a tab held open across either New Zealand date boundary.
      this.windowTimer = setInterval(() => this.updateWindow(), 1000);
      this.refreshRotation();
    }
    equalizeSlideHeight() {
      this.style.removeProperty('--aa-lw-slide-height');
      let height = 0;
      this.slides.forEach(slide => {
        const wasHidden = slide.hidden;
        slide.hidden = false;
        height = Math.max(height, slide.getBoundingClientRect().height);
        slide.hidden = wasHidden;
      });
      this.style.setProperty('--aa-lw-slide-height', `${Math.ceil(height)}px`);
    }
    updateWindow() {
      const now = Date.now();
      const start = Date.parse(this.dataset.start);
      const end = Date.parse(this.dataset.end);
      const valid = Number.isFinite(start) && Number.isFinite(end) && start < end;
      const live = valid && now >= start && now < end && this.dataset.ready === 'true';
      const coming = valid && now < start && this.dataset.coming === 'true';
      const nextState = live ? 'live' : coming ? 'coming' : 'hidden';
      if (this.dataset.state === nextState) return;
      this.dataset.state = nextState;
      this.hidden = nextState === 'hidden';
      if (this.hidden) {
        this.controls.hidden = true;
      } else {
        this.controls.hidden = this.slides.length < 2;
        this.phase.textContent = live ? 'Ends 28 October, 11:59pm NZDT' : 'Coming 20 October';
        this.querySelectorAll('[data-lw-terms]').forEach(item => {
          item.textContent = live ? 'Discount applied at checkout · Prices in NZD' : '20–28 October only · Discount applied at checkout · NZD';
        });
      }
      this.refreshRotation();
    }
    go(index, manual) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        slide.hidden = position !== this.index;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${position + 1} of ${this.slides.length}`);
      });
      this.dots.forEach((dot, position) => dot.setAttribute('aria-current', String(position === this.index)));
      if (manual) this.userPaused = true;
      this.refreshRotation();
    }
    refreshRotation() {
      clearInterval(this.rotationTimer);
      if (!this.toggle || !this.viewport) return;
      const running = !this.hidden && !this.userPaused && !this.hovering && !document.hidden && !this.motion.matches && this.slides.length > 1;
      this.toggle.textContent = this.userPaused ? 'Play' : 'Pause';
      this.toggle.setAttribute('aria-label', this.userPaused ? 'Start offer rotation' : 'Pause offer rotation');
      this.toggle.hidden = this.motion.matches;
      this.viewport.setAttribute('aria-live', running ? 'off' : 'polite');
      if (running) this.rotationTimer = setInterval(() => this.go(this.index + 1, false), 5000);
    }
  }
  customElements.define('aroha-labour-weekend', ArohaLabourWeekend);
})();
